import { Show, createEffect, createMemo, createSignal, on } from "solid-js";

import { GetUnread } from "@/api/clientApi/notice.ts";
import { Button } from "@/components/Base/Button/Button.tsx";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import Icon from "@/components/Icon";
import { getImageAltByLang } from "@/constants/seo-constants.ts";
import { useGlobalSearch } from "@/hooks/useGlobalSearch.ts";
import {
  notificationStore,
  setNotificationStore,
} from "@/hooks/useNotification.ts";
import { useRouter } from "@/hooks/useRouter.tsx";
import { useI18n } from "@/i18n/index";
import { sdkManager } from "@/lib/sdk/sdkManager.ts";
import { sidebarStore } from "@/stores/sidebar.ts";
import { sidebarContainerStore } from "@/stores/sidebarContainerStore.ts";
import { sysConfigStore } from "@/stores/sysConfigStore.ts";
import { userStore } from "@/stores/userStore.ts";
import { useLocation } from "@solidjs/router";

import BonusDropdownMenu from "./BonusDropdownMenu.tsx";
import UserDropdownMenu from "./UserDropdownMenu.tsx";
import WalletDropdownMenu from "./WalletDropdownMenu.tsx";

export default function PCHeader(props: { isLandingPage?: boolean }) {
  const { t } = useI18n();
  const { showDialog } = useDialog();
  const location = useLocation();
  const { push, toDeposit, preCheckEnterGame } = useRouter();
  const envName = import.meta.env.VITE_ENV_NAME;
  const count = createMemo(() => notificationStore.count);

  createEffect(
    on(
      () => userStore.isLogin,
      (isLogin) => {
        if (isLogin) {
          console.log("get unread");
          GetUnread({ msg_type: 0 }).then((res) => {
            const count = (res?.unread?.[1] || 0) + (res?.unread?.[2] || 0);
            setNotificationStore("count", count);
          });
        }
      },
    ),
  );

  const { openGlobalSearchModal } = useGlobalSearch();

  const [activeTab, setActiveTab] = createSignal<number | null>(null);

  const isLogin = createMemo(() => userStore.isLogin);

  createEffect(
    on(
      createMemo(() => location.pathname),
      () => {
        if (location.pathname.includes("casino")) {
          setActiveTab(0);
        } else if (location.pathname.includes("sports")) {
          setActiveTab(1);
        } else {
          setActiveTab(null);
        }
      },
    ),
  );

  // 处理游戏类型标签切换
  async function handleTabChange(tab: number) {
    const defaultSportGameId = sysConfigStore.sport_config.sport_game_id;

    if (tab == 1) {
      if (userStore.isLogin) {
        const pass = await preCheckEnterGame({
          gid: `${defaultSportGameId}`,
          // itemId: `${props?.itemId}`,
          // bindPointType: bindPointType,
        });
        if (!pass) {
          return;
        }
      }

      push(`/sports/${defaultSportGameId}`);
      sdkManager.tdReportEvent("menu_page_sports_entrance_click", {});
    } else {
      push("/casino");
      sdkManager.tdReportEvent("menu_page_casino_entrance_click", {});
    }
    setActiveTab(tab);
  }

  return (
    <header
      // --notification-width
      class={`bg-general-up-di z-[1000] flex ${sidebarContainerStore.state.isExpanded ? "w-[calc(100%-400px)]" : ""}`}
    >
      <div
        class={`flex h-full items-center justify-between px-[12px] ${!props?.isLandingPage && sidebarStore.state.isExpanded ? "border-menu-sk w-[240px] justify-start border-r-[1px]" : "w-[72px] justify-center"}`}
      >
        <Show when={!props?.isLandingPage} fallback={<></>}>
          <div
            onClick={sidebarStore.toggleSidebar}
            class="flex w-[20px] cursor-pointer items-center justify-center"
          >
            <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
              <Icon
                name="general/menu_open"
                class="text-menu-darkzi h-[20px] w-[20px]"
              />
            </Show>
            <Show when={!sidebarStore.state.isExpanded} fallback={<></>}>
              <Icon
                name="general/menu_close"
                class="text-menu-darkzi h-[20px] w-[20px] rotate-[180deg]"
              />
            </Show>
          </div>

          <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
            <div class="bg-menu-bg1 ml-[8px] flex h-[36px] w-[188px] items-center justify-center gap-[4px] rounded-sm p-[2px]">
              <div
                class={`group flex h-[32px] flex-1 cursor-pointer items-center justify-center gap-1 rounded-sm py-[6px] ${activeTab() === 0 ? "from-general-main to-general-assist bg-gradient-to-r" : "hover:text-general-light"}`}
                onClick={() => handleTabChange(0)}
              >
                <Icon
                  name="general/casino_big"
                  class={`h-[18px] w-[18px] ${activeTab() === 0 ? "text-general-butzi" : "text-menu-darkzi group-hover:text-general-light"}`}
                  wrapperClass="w-[20px] h-[20px]"
                />

                <span
                  class="group mt-[1px] text-[13px] leading-[17px]"
                  classList={{
                    "text-general-butzi font-[700]": activeTab() === 0,
                    "text-menu-darkzi group-hover:text-general-light font-[500]":
                      activeTab() !== 0,
                  }}
                >
                  {t("home.casino")}
                </span>
              </div>

              <div
                class={`group flex h-[32px] flex-1 cursor-pointer items-center justify-center gap-1 rounded-sm ${activeTab() === 1 ? "from-general-main to-general-assist bg-gradient-to-r" : ""}`}
                onClick={() => handleTabChange(1)}
              >
                <Icon
                  name="general/sports_big"
                  class={`h-[18px] w-[18px] rotate-[180deg] ${activeTab() === 1 ? "text-general-butzi" : "text-menu-darkzi group-hover:text-general-light"}`}
                  wrapperClass="w-[20px] h-[20px]"
                />

                <span
                  class="group mt-[1px] text-[13px] leading-[17px]"
                  classList={{
                    "text-general-butzi font-[700]  ": activeTab() === 1,
                    "text-menu-darkzi group-hover:text-general-light font-[500]":
                      activeTab() !== 1,
                  }}
                >
                  {t("home.sports")}
                </span>
              </div>
            </div>
          </Show>
        </Show>
      </div>
      <div class="mx-auto max-w-[1200px] flex-1">
        <div class="header-content">
          {/* <Link href="/" class="clickable flex"> */}
          <img
            src={
              envName === "prod"
                ? "/images/logo/logo.png"
                : "/images/kapok/logo.png"
            }
            width={96}
            height={42}
            alt={getImageAltByLang("logo")}
            class="clickable mb-[auto] mt-[auto] h-[42px] w-[96px]"
            onClick={() => {
              sdkManager.tdReportEvent("afun_homepage_click", {
                from_page: window.location.pathname,
              });
              push("/");
            }}
          />
          {/* </Link> */}

          {/* 搜索框 */}
          <Show when={!props?.isLandingPage} fallback={<></>}>
            <div
              class="clickable flex h-[43px] min-w-[80px] max-w-[360px] flex-1 items-center rounded-[22px] bg-[#111412] px-[12px]"
              onclick={openGlobalSearchModal}
            >
              <Icon
                name="search"
                class="text-up-icon1 h-[16.66px] w-[15.66px]"
                wrapperClass="w-[22px] h-[22px]"
              />
            </div>
          </Show>

          <div
            class={`flex items-center gap-[12px] ${sidebarContainerStore.state.isExpanded ? "pr-6" : ""}`}
          >
            <Show when={isLogin()} fallback={<></>}>
              <Show when={!props?.isLandingPage} fallback={<></>}>
                <div class="bg-up-icondi border-up-stroke flex h-[42px] w-auto items-center rounded-sm border p-[4px]">
                  <WalletDropdownMenu />
                  <div
                    class="bg-linear-90 from-up-mainbut-l to-up-mainbut-r text-up-butzi clickable flex h-[34px] w-auto min-w-[96px] items-center gap-1 rounded-[6px] px-[10px] text-[14px] font-bold leading-[17px]"
                    onclick={() => toDeposit()}
                  >
                    <Icon
                      name="general/bottom_withdrawal"
                      class="text-up-butzi h-[18px] w-[18px]"
                    />
                    <span class="text-up-butzi text-[14px] font-[700] leading-[17px]">
                      {t("affiliate.deposit")}
                    </span>
                  </div>
                </div>
              </Show>

              {/* 奖金中心 */}
              <div class={`${isLogin() ? "mr-[6px]" : ""}`}>
                <BonusDropdownMenu />
              </div>
            </Show>
            {/* 用户中心 */}
            <Show when={isLogin()} fallback={<></>}>
              <UserDropdownMenu />
            </Show>

            <Show when={!isLogin()} fallback={<></>}>
              {/* <Icon
                name="general/search"
                class="text-up-icon1 group-hover:text-general-light h-[18px] w-[18px]"
                wrapperClass="bg-up-icondi clickable h-[40px] w-[40px] group rounded-md "
                onClick={openGlobalSearchModal}
              /> */}
              <Show when={!props?.isLandingPage} fallback={<></>}>
                <BonusDropdownMenu />
              </Show>
              <Button
                class="h-[40px] w-fit rounded-[8px] px-[18.3px] text-[15px] font-[800] leading-[24px]"
                variant="default"
                size="default"
                onClick={() => {
                  sdkManager.tdReportEvent("signin_button_click", {
                    from_page: window.location.pathname,
                  });
                  push("login");
                }}
              >
                {t("auth.sign_in")}
              </Button>
              <Button
                class="text-general-main bg-general-main10 h-[40px] w-fit rounded-[8px] px-[19.5px] text-[15px] font-[800] leading-[24px]"
                size="default"
                onClick={() => {
                  sdkManager.tdReportEvent("signup_button_click", {
                    from_page: window.location.pathname,
                  });
                  push("register");
                }}
              >
                {t("auth.sign_up")}
              </Button>
            </Show>

            <Show when={isLogin() && !props?.isLandingPage} fallback={<></>}>
              <div
                class="bg-up-icondi clickable group relative flex h-[40px] w-[40px] items-center justify-center rounded-[8px]"
                onClick={sidebarContainerStore.toggleSidebar}
              >
                <Icon
                  // prettier-ignore
                  name="general/notify3"
                  class={`group-hover:text-general-light ${sidebarContainerStore.state.isExpanded ? "text-general-light" : "text-up-icon1"} clickable h-[18px w-[18px]`}
                />

                <Show when={count() > 0} fallback={<></>}>
                  <div class="bg-general-main absolute right-[6px] top-[6px] h-[6px] w-[6px] rounded-full" />
                </Show>
              </div>
            </Show>

            <Show when={!props?.isLandingPage} fallback={<></>}>
              <Icon
                name="general/globe"
                class="text-up-icon1 group-hover:text-general-light h-[18px] w-[18px]"
                wrapperClass="bg-up-icondi clickable h-[40px] w-[40px] group  rounded-[8px]"
                onClick={() => showDialog({ type: DialogType.SwitchLanguage })}
              />
            </Show>
          </div>
        </div>
      </div>
    </header>
  );
}
